import { Box, Grid, GridItem, chakra } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { FEATURE_FLAGS } from '~/constants';
import { CREATOR_MANAGER_RELATION } from '~/constants/creatorManagerRelation';
import { SupportedCharts, SupportedPlatforms } from '~/constants/platforms';
import { useGetCreatorProfile } from '~/hooks/api/useGetCreatorProfile';
import { useCurrentUserCreatorRelation } from '~/hooks/useCurrentUserCreatorRelation';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { usePlatformMetrics } from '~/hooks/usePlatformMetrics';
import { EmptyState } from '~/screens/CreatorAudienceDemographics';
import { AgeDistribution } from '~/screens/CreatorAudienceDemographics/AgeDistribution';
import { CountryDistribution } from '~/screens/CreatorAudienceDemographics/CountryDistribution';
import { GenderDistribution } from '~/screens/CreatorAudienceDemographics/GenderDistribution';
import { CreatorType } from '~/types';

import { DistributionWrapper } from './DistributionWrapper';
import { PlatformChartsCaption } from './PlatformChartsCaption';

interface Props {
  platform: SupportedPlatforms;
  demographics?: SupportedCharts[];
  creatorSlug?: string;
  isBuilder?: boolean;
}

export const PlatformCharts = ({
  platform,
  demographics,
  creatorSlug,
  isBuilder = false,
}: Props) => {
  const router = useRouter();
  const slug = router.query.creatorSlug as string;
  const creator = useGetCreatorProfile({ slug: creatorSlug || slug });
  const foamSmartTalentMetricsFeatureFlag = useFeature(
    FEATURE_FLAGS.foamSmartTalentMetrics,
  );
  const {
    isEmpty,
    showEmptyState,
    isPlatformConnected,
    isSTMPredictedVisible,
    isPublicDemographicsVisible,
    insights,
  } = usePlatformMetrics({ creator: creator.data, platform });

  const relationToCreator = useCurrentUserCreatorRelation(
    creator.data as CreatorType,
  );
  const isManager =
    relationToCreator === CREATOR_MANAGER_RELATION.coManager ||
    relationToCreator === CREATOR_MANAGER_RELATION.manager;

  const isPredictedVisible =
    isSTMPredictedVisible && foamSmartTalentMetricsFeatureFlag.isEnabled;
  /** TikTok public data renders like connected data, not as an STM estimate. */
  const hasRenderableData = isPlatformConnected || isPublicDemographicsVisible;

  return (
    <chakra.section>
      <Box position="relative">
        <Grid
          templateColumns="repeat(auto-fit, minmax(360px, 1fr))"
          gap={6}
          alignItems="stretch"
        >
          {(!isBuilder || demographics?.includes('gender')) && (
            <GridItem>
              <DistributionWrapper>
                <GenderDistribution
                  insightId={insights?.insightId}
                  platform={platform}
                  isConnected={hasRenderableData || isPredictedVisible}
                />
              </DistributionWrapper>
            </GridItem>
          )}

          {(!isBuilder || demographics?.includes('age')) && (
            <GridItem>
              <DistributionWrapper>
                <AgeDistribution
                  insightId={insights?.insightId}
                  platform={platform}
                  isConnected={hasRenderableData || isPredictedVisible}
                />
              </DistributionWrapper>
            </GridItem>
          )}

          {(!isBuilder || demographics?.includes('country')) && (
            <GridItem>
              <DistributionWrapper>
                <CountryDistribution
                  insightId={insights?.insightId}
                  platform={platform}
                  isConnected={hasRenderableData}
                  isPredictedVisible={isPredictedVisible}
                  creator={creator.data}
                />
              </DistributionWrapper>
            </GridItem>
          )}
        </Grid>

        {showEmptyState ? (
          <EmptyState
            isEmpty={isEmpty}
            isConnected={isPlatformConnected}
            isManager={isManager}
          />
        ) : null}
      </Box>

      <PlatformChartsCaption creator={creator.data} platform={platform} />
    </chakra.section>
  );
};
